import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Layout components
import Layout from "./components/Layout/Layout";

// Main Pages
// Removed top-level Dashboard import as we're reorganizing the structure
import TechnicalAnalysis from "./pages/TechnicalAnalysis";
import FundamentalAnalysis from "./pages/FundamentalAnalysis";
import RiskManagement from "./pages/RiskManagement";
import TradeHistory from "./pages/TradeHistory";
import Execution from "./pages/Execution";
import StrategyBuilder from "./pages/StrategyBuilder";
import Documentation from "./pages/Documentation";
import LandingPage from "./pages/LandingPage";
import TradingDashboard from "./pages/TradingDashboard";
import StrategyDashboard from "./pages/strategy/StrategyDashboard";

// Auth Pages
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";

// User Pages
import UserDashboard from "./pages/user/Dashboard";
import UserPlans from "./pages/user/Plans";
import UserTickets from "./pages/user/Tickets";
import UserSettings from "./pages/user/Settings";
import ApiConnections from "./pages/user/ApiConnections";
import TradingViewWebhook from "./pages/user/TradingViewWebhook";
import Monitor from "./pages/Monitor";
import MonitorErrorBoundary from "./components/MonitorErrorBoundary";
import { installCapture } from "./services/MonitorCapture";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminPlans from "./pages/admin/Plans";
import AdminTickets from "./pages/admin/Tickets";
import AdminUsers from "./pages/admin/Users";
import AdminBlog from "./pages/admin/Blog";
import AdminPayments from "./pages/admin/Payments";
import AdminNewsletter from "./pages/admin/Newsletter";
import AdminMarketing from "./pages/admin/Marketing";
import AdminSettings from "./pages/admin/Settings";

// Install monitor capture (console/error interception) — dev tool
if (process.env.REACT_APP_ENABLE_MONITOR_PAGE !== 'false') {
  installCapture();
}

// Create theme — glassmorphic dark neon design system
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#00FF94",
      light: "#33FFB0",
      dark: "#00CC76",
    },
    secondary: {
      main: "#3b82f6",
    },
    success: {
      main: "#00FF94",
    },
    error: {
      main: "#EF4444",
    },
    warning: {
      main: "#FBBF24",
    },
    info: {
      main: "#3b82f6",
    },
    background: {
      default: "#0d1117",
      paper: "rgba(22,27,34,0.75)",
    },
    text: {
      primary: "#F3F4F6",
      secondary: "#9CA3AF",
    },
    divider: "rgba(255,255,255,0.06)",
  },
  typography: {
    fontFamily: 'Inter, Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0d1117',
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { background: '#0d1117' },
          '&::-webkit-scrollbar-thumb': { background: '#3a3f47', borderRadius: 3 },
          '&::-webkit-scrollbar-thumb:hover': { background: '#555d68' },
        },
        '*::-webkit-scrollbar': { width: 6, height: 6 },
        '*::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.2)' },
        '*::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.12)', borderRadius: 3 },
        '*::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.2)' },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 4,
          fontWeight: 600,
        },
        contained: {
          background: 'linear-gradient(135deg, #00FF94 0%, #00CC76 100%)',
          color: '#000',
          '&:hover': {
            background: 'linear-gradient(135deg, #33FFB0 0%, #00FF94 100%)',
            boxShadow: '0 0 20px rgba(0,255,148,0.3)',
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.25)',
            backgroundColor: 'rgba(255,255,255,0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundImage: 'none',
          backgroundColor: 'rgba(22,27,34,0.75)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.4)',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.15)',
            backgroundColor: 'rgba(30,35,44,0.82)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(22,27,34,0.75)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 4px 30px rgba(0,0,0,0.4)',
          transition: 'border-color 0.3s ease',
          '&:hover': {
            borderColor: 'rgba(255,255,255,0.12)',
          },
          '@media (max-width:600px)': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'rgba(13,17,23,0.8)',
            color: 'rgba(255,255,255,0.6)',
            fontWeight: 700,
            fontSize: '0.65rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          },
          '& .MuiTableCell-stickyHeader': {
            backgroundColor: 'rgba(13,17,23,0.9)',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.03) !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.04)',
          fontSize: '0.8rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(22,27,34,0.92)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          '@media (max-width:600px)': {
            margin: 16,
            width: 'calc(100% - 32px)',
            maxHeight: 'calc(100% - 32px)',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: 'rgba(255,255,255,0.4)',
          '&.Mui-selected': {
            color: '#00FF94',
            fontWeight: 600,
          },
          '@media (max-width:600px)': {
            minWidth: 'auto',
            padding: '6px 12px',
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        },
        indicator: {
          backgroundColor: '#00FF94',
          height: 2,
          boxShadow: '0 0 8px rgba(0,255,148,0.5)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.75rem',
          color: 'rgba(255,255,255,0.4)',
          borderColor: 'rgba(255,255,255,0.1)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(255,255,255,0.1)',
            color: '#fff',
            borderColor: 'rgba(255,255,255,0.2)',
            boxShadow: '0 0 8px rgba(255,255,255,0.05)',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.14)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.05)',
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 4,
          padding: 2,
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '4px !important',
            margin: 1,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          '@media (max-width:600px)': {
            height: 24,
            fontSize: '0.7rem',
          },
        },
        outlined: {
          borderColor: 'rgba(255,255,255,0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255,255,255,0.02)',
            '& fieldset': {
              borderColor: 'rgba(255,255,255,0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255,255,255,0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00FF94',
              boxShadow: '0 0 8px rgba(0,255,148,0.15)',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.1)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.2)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00FF94',
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(22,27,34,0.7)',
          border: '1px solid rgba(255,255,255,0.06)',
          '&:before': { display: 'none' },
          '&.Mui-expanded': {
            margin: 0,
            borderColor: 'rgba(255,255,255,0.1)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: '1px solid',
          backdropFilter: 'blur(8px)',
        },
        standardSuccess: {
          backgroundColor: 'rgba(0,255,148,0.08)',
          borderColor: 'rgba(0,255,148,0.2)',
          color: '#00FF94',
        },
        standardError: {
          backgroundColor: 'rgba(239,68,68,0.08)',
          borderColor: 'rgba(239,68,68,0.2)',
          color: '#EF4444',
        },
        standardWarning: {
          backgroundColor: 'rgba(251,191,36,0.08)',
          borderColor: 'rgba(251,191,36,0.2)',
          color: '#FBBF24',
        },
        standardInfo: {
          backgroundColor: 'rgba(59,130,246,0.08)',
          borderColor: 'rgba(59,130,246,0.2)',
          color: '#3b82f6',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.06)',
          borderRadius: 2,
        },
        bar: {
          borderRadius: 2,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,0.06)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(22,27,34,0.92)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.1)',
          fontSize: '0.75rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        },
        arrow: {
          color: 'rgba(22,27,34,0.92)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: 'rgba(22,27,34,0.92) !important',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1) !important',
          boxShadow: '0 8px 40px rgba(0,0,0,0.5) !important',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          '& .MuiSwitch-switchBase.Mui-checked': {
            color: '#00FF94',
            '& + .MuiSwitch-track': {
              backgroundColor: '#00FF94',
              opacity: 0.4,
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgba(255,255,255,0.06)',
        },
      },
    },
  },
});

// Helper function for role-based routing
const RoleBasedRedirect = () => {
  // In a real app, this would check auth context
  // For demo, we're setting admin as the role
  const userRole = "admin"; // or 'user'

  // Redirect to strategy builder for quick access to strategy management
  if (userRole === "admin") {
    return <Navigate to="/user/strategy-builder" replace />;
  } else {
    return <Navigate to="/user/strategy-builder" replace />;
  }
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public Routes - No layout */}
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Auth Routes - Backward compatibility */}
          <Route
            path="/auth/login"
            element={<Navigate to="/login" replace />}
          />
          <Route
            path="/auth/register"
            element={<Navigate to="/register" replace />}
          />

          {/* Root redirect - sends to appropriate dashboard based on role */}
          <Route path="/" element={<RoleBasedRedirect />} />

          {/* User Area - completely separate section */}
          <Route path="/user" element={<Layout />}>
            <Route path="dashboard" element={<UserDashboard />} />
            <Route path="plans" element={<UserPlans />} />
            <Route path="tickets" element={<UserTickets />} />
            <Route path="settings" element={<UserSettings />} />
            <Route path="api-connections" element={<ApiConnections />} />

            {/* Trading tools accessible from user area */}
            <Route path="technical" element={<TechnicalAnalysis />} />
            <Route path="fundamental" element={<FundamentalAnalysis />} />
            <Route path="risk" element={<RiskManagement />} />
            <Route path="history" element={<TradeHistory />} />
            <Route path="execution" element={<Execution />} />
            <Route path="strategy-builder" element={<StrategyBuilder />} />
            <Route
              path="tradingview-webhook"
              element={<TradingViewWebhook />}
            />
            <Route path="documentation" element={<Documentation />} />
            <Route path="analytics" element={<TradingDashboard />} />
            <Route path="strategy-dashboard" element={<StrategyDashboard />} />
            {process.env.REACT_APP_ENABLE_MONITOR_PAGE !== 'false' && (
              <Route path="monitor" element={<MonitorErrorBoundary><Monitor /></MonitorErrorBoundary>} />
            )}
          </Route>

          {/* Admin Area - completely separate section */}
          <Route path="/admin" element={<Layout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="newsletter" element={<AdminNewsletter />} />
            <Route path="marketing" element={<AdminMarketing />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="analytics" element={<TradingDashboard />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
